/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { actions } from '@/redux/av-check';
import ApiService from '@/services';
import {
  AddressType,
  AvailabilityCheckPageValueType,
  AvCheckFormData,
  AvCheckRequestBody,
  CmsPageContentIdentifier,
  Feature,
  HeroOfferData,
  HouseholdConfigurationType, IndexType,
  ReduxStateType,
  ResultType,
  TariffOfferType,
} from '@/types/av-check';

import Head from '@/components/av-check/Head';
import AvailabilityChecker from '@/components/av-check/AvailabilityChecker';
import PositiveResult from '@/components/av-check/PositiveResult';
import TargetGroupSwitcher from '@/components/av-check/TargetGroupSwitcher';
import TariffCatalog from '@/components/av-check/TariffCatalog';
import ContractDurationSwitcher from '@/components/av-check/ContractDurationSwitcher';
import Faq from '@/components/av-check/Faq';
import ResultLoadingOverlay from '@/components/av-check/ResultLoadingOverlay';
import StaticContentModal from '@/components/modal/static-content-modal';
import {
  buildDataForInitialization,
  buildErrorData,
  buildWarningData,
  pushToTefDataLayer,
} from '@/services/tracking';

export interface AvCheckProps {
  serviceUrl: string,
  shopContext: string,
  selectedPack: string,
  selectedRouter: string,
  hardwareOfferName: string,
}

export interface StateProps {
  loadingOverlayTitle: string
  loadingOverlayMessage: string
  result?: ResultType,
  tariffOffers: TariffOfferType[],
  isCciTheme: boolean,
  dtagFtthLoadingOverlayTitle : string,
  heroOffer: HeroOfferData | undefined,
}

export interface DispatchProps {
  setAddress: Function,
  setHouseholdConfig: Function,
  setPageValue: Function,
  setResult: Function,
  setResultError: Function,
  setCityList: Function,
  setStreetList: Function,
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'tef-alert': OcclAlertInterface;
      'tef-promo-box':OcclPromoBoxComponent;
      'tef-tooltip': OcclToolTipComponent;
    }

    interface OcclAlertInterface {
      children?: Element | Element[];
      type?: string;
    }
    interface OcclPromoBoxComponent {
      children?: Element | Element[];
      cloak?: true;
      "color-scheme"?: string;
    }
    interface OcclToolTipComponent {
      children?: Element[];
      trigger?: string;
      placement?: string;
      positioning?: string;
      cloak?: true;
      plain?: true;
    }
  }
}

const AvCheck = ({
  setAddress,
  setHouseholdConfig,
  setPageValue,
  setResult,
  setResultError,
  setCityList,
  setStreetList,
  loadingOverlayTitle,
  loadingOverlayMessage,
  result,
  tariffOffers,
  serviceUrl,
  shopContext,
  selectedPack,
  selectedRouter,
  hardwareOfferName,
  isCciTheme,
  dtagFtthLoadingOverlayTitle,
  heroOffer,
}: AvCheckProps & StateProps & DispatchProps) => {
  const apiService = new ApiService(serviceUrl);
  const RESULT_LOADING_MODAL_ID = 'result-loading-modal';
  const DTAG_FTTH_RESULT_LOADING_MODAL_ID = 'dtag-ftth-result-loading-modal';
  const [isRedirectedFromFtthSelection, setRedirectedFromFtthSelection] = useState(false);
  const [showExpansionStatus, setShowExpansionStatus] = useState('');

  const trackPageInitialization = (pageValue: AvailabilityCheckPageValueType) => {
    const isUnifiedTrackingEnabled: boolean = (pageValue.enabledFeatures?.includes(Feature.UNIFIED_TRACKING)) || false;

    if (isUnifiedTrackingEnabled) {
      pushToTefDataLayer(window, buildDataForInitialization());
    }
  };

  useEffect(
    () => {

      let indexType = IndexType.INITIAL;

      apiService.getPageValue()
        .then((pageValue) => {
          setPageValue(pageValue);
          if (pageValue != undefined) {
            heroOffer = pageValue.meta?.heroOfferData;
            if (!!pageValue.index && pageValue.index === IndexType.ERROR){
              indexType = pageValue.index;
              setResultError(true);
              setAddress(pageValue.address);
            }
            if (!!pageValue.index && pageValue.index === IndexType.NO_OFFER){
              indexType = pageValue.index;
              setResult(apiService.getEmptyResult(true, pageValue.address));
              setAddress(pageValue.address);
            }
            trackPageInitialization(pageValue);
          }
        });

      if (indexType === IndexType.INITIAL) {
        apiService.getStoredResult(shopContext)
          .then((avCheckResult) => {
            if (avCheckResult) {
              setResult(avCheckResult);
              setAddress(avCheckResult.address);
              setHouseholdConfig(avCheckResult.householdConfiguration);

              if(avCheckResult.tariffOffers.length !== 0) {

                // @ts-ignore
                avCheckResult.tariffOffers.forEach((tariffOffer, any) => {
                  if (tariffOffer.accessType === "DTAG-FTTH-ETH" && tariffOffer.technologyAvailability !== undefined) {
                    const technologyAvailability = tariffOffer.technologyAvailability;
                    setShowExpansionStatus(technologyAvailability.expansionStatus);
                  }
                });
              }
              const { zipCode, city } = avCheckResult.address;
              apiService.getCitiesByZipcode(zipCode).then((cities) => {
                setCityList(cities);
              });
              apiService.getStreetsByCity(zipCode, city).then((streets) => {
                setStreetList(streets);
              });
            }
          })
          .catch((err) => {
            console.error('Unable to get stored result:', err);
          });
      }
    },
    [],
  );

  const openLoadingModal = (open: boolean) => {
    ($(`#${RESULT_LOADING_MODAL_ID}`) as any).modal(open ? 'show' : 'hide');
  };

  function matelsoScript() {
    let matelsoElement = document.getElementById('matelso');
    if (!matelsoElement) {
      let script = document.createElement('script');
      script.setAttribute('id', 'matelso');
      let scriptText = document.createTextNode('function matelsoScan(){' +
          'if (typeof mtls !== "undefined" && mtls.loaded)\n' +
          '{ mtls.scan(); }}');
      script.appendChild(scriptText);
      document.getElementsByTagName('head')[0].appendChild(script);
    }
    // @ts-ignore
    window.matelsoScan();
  }
  const onAvailabilityCheck = (avCheckFormData: AvCheckFormData) => {
    setResult(undefined);
    setResultError(false);
    openLoadingModal(true);

    let avCheckPending = true;
    let avCheckResult: ResultType;
    let resultError = false;

    const startTimeout = (timeout: number) => {
      setTimeout(() => {
        if (!avCheckPending) {
          if (resultError) {
            setResultError(resultError);
          } else {
            setResult(avCheckResult);
            if (avCheckResult.address) {
              setAddress(avCheckResult.address);
            }

            if (avCheckResult.householdConfiguration) {
              setHouseholdConfig(avCheckResult.householdConfiguration);
            }
          }
          openLoadingModal(false);
        } else {
          // in the case that av check takes longer than 3s
          // timeout will be increased by 1s until the av check is completed
          startTimeout(1000);
        }
      }, timeout);
    };

    const avCheckRequestBody = new AvCheckRequestBody(avCheckFormData, heroOffer);

    // 3s as initial timeout is needed to make sure the loading modal will be shown
    // at least 5s whenever the av result is returned
    startTimeout(3000);

    apiService.checkAvailability(
      avCheckRequestBody,
      shopContext,
      selectedPack,
      selectedRouter,
      hardwareOfferName,
    )
      .then((resultResponse) => {
        avCheckPending = false;
        avCheckResult = resultResponse as any;

        if (avCheckResult.address) {
          const resultAddress = `${avCheckResult.address.zipCode} ${avCheckResult.address.city}`;
          if (avCheckResult.tariffOffers.length === 0) {
            matelsoScript();
            pushToTefDataLayer(window, buildWarningData('no offers found!', resultAddress));
          } else {
            avCheckResult.tariffOffers.forEach((tariffOffer, any) => {
              if (tariffOffer.accessType === "DTAG-FTTH-ETH" && tariffOffer.technologyAvailability !== undefined) {
                const technologyAvailability = tariffOffer.technologyAvailability;
                setShowExpansionStatus(technologyAvailability.expansionStatus);
              }
            });
          }
        } else {
          matelsoScript();
          pushToTefDataLayer(window, buildWarningData('no address found!', `${avCheckFormData.zipCode} ${avCheckFormData.city}`));
        }
      })
      .catch((err) => {
        avCheckPending = false;
        resultError = true;
        console.error('Unable to check availability:', err);
        matelsoScript();
        pushToTefDataLayer(window, buildErrorData(err.message, avCheckRequestBody));
      });
  };
  const targetGroupSwitchable = tariffOffers
    .map((item) => item.targetGroup)
    .filter((value, index, self) => self.indexOf(value) === index)
    .length > 1;

  return (
    <>
      <StaticContentModal displayCloseButton={false} openerId={RESULT_LOADING_MODAL_ID}>
        <ResultLoadingOverlay
          title={loadingOverlayTitle}
          message={loadingOverlayMessage}
        />
      </StaticContentModal>
      <StaticContentModal displayCloseButton={false} openerId={DTAG_FTTH_RESULT_LOADING_MODAL_ID}>
        <ResultLoadingOverlay
            title={dtagFtthLoadingOverlayTitle}
            message={loadingOverlayMessage}
        />
      </StaticContentModal>
      <Head />
      { !result || !result.address || tariffOffers.length === 0 || result.address.corrected
        ?
        (!isRedirectedFromFtthSelection &&
          <AvailabilityChecker
            isPdpCall={false}
            onAvailabilityCheck={onAvailabilityCheck}
            onCityListFetch={(zipCode: string, onSuccess: (cities: string[]) => void) => (
              apiService.getCitiesByZipcode(zipCode, onSuccess)
            )}
            onStreetListFetch={
              (zipCode: string, city: string, onSuccess: (streets: string[]) => void) => (
                apiService.getStreetsByCity(zipCode, city, onSuccess)
              )
            }
            displayHouseholdConfigurator={true}
          />
        )
        : (
          <>
            <PositiveResult />
            {targetGroupSwitchable && <TargetGroupSwitcher />}
            {isCciTheme && <ContractDurationSwitcher />}
            <TariffCatalog
                isRedirectedFromFtthSelection={isRedirectedFromFtthSelection}
                setRedirectedFromFtthSelection={setRedirectedFromFtthSelection}
                expansionStatus={showExpansionStatus}
                serviceUrl={serviceUrl}
                shopContext={shopContext}
                selectedPack={selectedPack}
                selectedRouter={selectedRouter}
                hardwareOfferName={hardwareOfferName}
                isCmsCall = {result.isCmsCall}
                isCciTheme = {isCciTheme}
            />
            {isCciTheme || <ContractDurationSwitcher />}
          </>
        )}
      <Faq />
    </>
  );
};

const mapStateToProps = (state: ReduxStateType) => ({
  loadingOverlayTitle: state.cmsContent[CmsPageContentIdentifier.LoadingOverlayTitle],
  loadingOverlayMessage: state.cmsContent[CmsPageContentIdentifier.LoadingOverlayMessage],
  result: state.result,
  tariffOffers: state.result?.tariffOffers || [],
  isCciTheme: state.meta?.cciTheme || false,
  dtagFtthLoadingOverlayTitle : state.cmsContent[CmsPageContentIdentifier.DtagFtthLoadingOverlayTitle],
  heroOffer: state.meta?.heroOfferData,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setAddress: (address: AddressType) => dispatch(actions.setAddress(address)),
  setHouseholdConfig: (householdConfig: HouseholdConfigurationType) => dispatch(actions.setHouseholdConfig(householdConfig)),
  setPageValue: (pageValue: AvailabilityCheckPageValueType) => dispatch(actions.setPageValue(pageValue)),
  setResult: (result: ResultType) => dispatch(actions.setResult(result)),
  setResultError: (isError: boolean) => dispatch(actions.setResultError(isError)),
  setCityList: (cities: string[]) => dispatch(actions.setCityList(cities)),
  setStreetList: (streets: string[]) => dispatch(actions.setStreetList(streets)),
});

export default connect<StateProps, DispatchProps, AvCheckProps, ReduxStateType>(
  mapStateToProps,
  mapDispatchToProps,
)(AvCheck);
